import React, { useContext, useMemo, useState } from 'react';
import { useTable, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import moment from 'moment';
import styled from 'styled-components';
import i18n, { langCode } from '../i18n';
import { formatDate } from '../utils/timeUtils';
import ThemeContext from '../contexts/ThemeContext';
import Icon from './Icon';
import {
  BookingsTableContainer,
  BookingTableThead,
  BookingTableTh,
  BookingTableTr,
  BookingTableTd,
  TrText,
  HeaderContainer,
  SortIconContainer,
  BookingsFilterSelect,
} from './basicComponents';

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 18px;
  margin-bottom: 18px;
  border: none;
  border-radius: 10px;
  background-color: #f3f3fb;
  font-size: ${({ fontSize }) => fontSize}px;
  &:focus {
    outline: none;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 6px 10px;
  color: ${({ color }) => color};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  font-size: ${({ fontSize }) => fontSize}px;
  white-space: nowrap;
`;

const RetentionNotice = styled.p`
  margin: 0 0 12px;
  color: ${({ color }) => color};
  font-size: ${({ fontSize }) => fontSize}px;
`;

// Anchored on the session start (`time`), not `closedAt`: closedAt can be stamped
// long after the session actually ran, which would keep the actions enabled while
// the transcript/summary files are already gone (TUL-5363).
const isDownloadExpired = (startedAt) => {
  if (!startedAt) {
    return false;
  }
  const expiresAt = moment(startedAt);
  return expiresAt.isValid() && moment().isSameOrAfter(expiresAt.add(24, 'hours'));
};

function SelectColumnFilter({ column: { filterValue = '', setFilter, preFilteredRows, id } }) {
  const { layout } = useContext(ThemeContext);

  const options = useMemo(() => {
    const optionsSet = new Set();
    preFilteredRows.forEach(row => {
      if (row.values[id]) {
        optionsSet.add(row.values[id]);
      }
    });
    return [...optionsSet.values()];
  }, [id, preFilteredRows]);

  return (
    <BookingsFilterSelect
      fontSize={layout.smallFontSize}
      value={filterValue}
      onChange={e => setFilter(e.target.value || undefined)}
    >
      <option value="">{i18n('all')}</option>
      {options.map((option, i) => (
        <option key={i} value={option}>{option}</option>
      ))}
    </BookingsFilterSelect>
  );
}

// Invoice refs may be numeric; react-table's built-in `includes` filter assumes
// every value implements String.prototype.includes and crashes on those rows.
const exactStringFilter = (rows, ids, filterValue) => rows.filter(row => (
  ids.some(id => String(row.values[id]) === String(filterValue))
));

const AiInterpreterTable = ({ sessions, onDownload, onSendEmail }) => {
  moment.locale(langCode);
  const { colors, layout } = useContext(ThemeContext);
  const [pending, setPending] = useState({});

  const runAction = async (key, action) => {
    if (pending[key]) {
      return;
    }
    setPending(prev => ({ ...prev, [key]: true }));
    try {
      await action();
    } finally {
      setPending(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const actionCell = (label, key, action, disabled = false) => (
    <ActionButton
      type="button"
      color={colors.webDarkBlue}
      fontSize={layout.smallFontSize}
      disabled={disabled || !!pending[key]}
      aria-describedby={disabled ? 'ai-download-retention-message' : undefined}
      onClick={() => runAction(key, action)}
    >
      {pending[key] ? i18n('please_wait') : label}
    </ActionButton>
  );

  const columns = useMemo(() => [
    {
      Header: i18n('time'),
      accessor: 'time',
      // `time` arrives as an ISO string, so react-table's built-in 'datetime' sort
      // (which calls value.getTime()) throws on click. Parse defensively with moment.
      sortType: (rowA, rowB, columnId) => {
        const a = moment(rowA.values[columnId]).valueOf();
        const b = moment(rowB.values[columnId]).valueOf();
        return a === b ? 0 : a > b ? 1 : -1;
      },
      disableFilters: true,
      Cell: ({ cell: { value } }) => {
        const date = moment(value);
        return `${formatDate(date)} ${date.format('HH:mm')}`;
      },
    },
    {
      Header: i18n('language_title'),
      accessor: 'sourceLanguage',
      Filter: SelectColumnFilter,
      filter: 'includes',
    },
    {
      Header: i18n('ai_target_language'),
      accessor: 'targetLanguage',
      Filter: SelectColumnFilter,
      filter: 'includes',
    },
    {
      Header: i18n('prebooking_duration'),
      accessor: 'durationMinutes',
      disableFilters: true,
      Cell: ({ cell: { value } }) => `${value} min`,
    },
    {
      Header: i18n('ai_cost_center'),
      accessor: 'costCenter',
      Filter: SelectColumnFilter,
      filter: 'includes',
    },
    {
      Header: i18n('invoice_ref'),
      accessor: 'invoiceRef',
      Filter: SelectColumnFilter,
      filter: exactStringFilter,
    },
    {
      Header: i18n('ai_download_transcript'),
      id: 'downloadTranscript',
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row: { original } }) =>
        actionCell(i18n('ai_transcription'), `${original.chatRoomId}:transcript`, () =>
          onDownload('transcript', original), isDownloadExpired(original.time)),
    },
    {
      Header: i18n('ai_download_summary'),
      id: 'downloadSummary',
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row: { original } }) =>
        actionCell(i18n('ai_summary'), `${original.chatRoomId}:summary`, () =>
          onDownload('summary', original), isDownloadExpired(original.time)),
    },
    {
      Header: i18n('ai_send_email'),
      id: 'sendEmail',
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row: { original } }) =>
        actionCell(i18n('ai_send_email'), `${original.chatRoomId}:email`, () =>
          onSendEmail(original), isDownloadExpired(original.time)),
    },
  ], [colors, layout, pending, onDownload, onSendEmail]); // eslint-disable-line react-hooks/exhaustive-deps -- columns use `actionCell` plus callback props; keep deps explicit to avoid stale closures

  const data = useMemo(() => sessions || [], [sessions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable({ columns, data, autoResetFilters: false, autoResetGlobalFilter: false }, useFilters, useGlobalFilter, useSortBy);

  return (
    <div>
      <RetentionNotice
        id="ai-download-retention-message"
        color={colors.webDarkBlue}
        fontSize={layout.smallFontSize}
      >
        {i18n('ai_download_retention')}
      </RetentionNotice>
      <SearchInput
        fontSize={layout.smallFontSize}
        value={state.globalFilter || ''}
        placeholder={i18n('ai_search_placeholder')}
        onChange={e => setGlobalFilter(e.target.value || undefined)}
      />
      <BookingsTableContainer fontSize={layout.smallFontSize} {...getTableProps()}>
        <BookingTableThead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()} valign="top">
              {headerGroup.headers.map((column, index) => (
                <BookingTableTh lang={langCode} key={index}>
                  <TrText fontSize={layout.smallFontSize} {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.canSort ? (
                      <SortIconContainer>
                        <Icon icon="sort" iconLib="material" iconColor={colors.webDarkBlue} iconSize={16} withoutContainer={true} />
                      </SortIconContainer>
                    ) : null}
                  </TrText>
                  <div>{column.canFilter && column.Filter ? column.render('Filter') : null}</div>
                </BookingTableTh>
              ))}
            </tr>
          ))}
        </BookingTableThead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <BookingTableTr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <BookingTableTd {...cell.getCellProps()}>{cell.render('Cell')}</BookingTableTd>
                ))}
              </BookingTableTr>
            );
          })}
        </tbody>
      </BookingsTableContainer>
      {rows.length === 0 ? (
        <HeaderContainer>{i18n('ai_no_sessions')}</HeaderContainer>
      ) : null}
    </div>
  );
};

export default AiInterpreterTable;
